import { Injectable, OnDestroy, computed, inject, signal } from '@angular/core'
import { Device } from '../models/Device'
import { AppStateService } from '../store/state/app/app.state.service'
import { DeviceService } from './device.service'

const GEO_PERMISSION_DENIED = 1

export interface LiveLocationWatchOptions {
  enableHighAccuracy?: boolean
  maximumAge?: number
  timeout?: number
  updateAppDevice?: boolean
}

@Injectable({
  providedIn: 'root'
})
export class LiveLocationService implements OnDestroy {
  readonly #appStateService = inject(AppStateService)
  readonly #deviceService = inject(DeviceService)

  readonly #position = signal<GeolocationPosition | null>(null)
  readonly #error = signal<GeolocationPositionError | null>(null)
  readonly #watching = signal(false)
  readonly #denied = signal(false)
  readonly #unsupported = signal(false)
  readonly #lastUpdatedAt = signal<Date | null>(null)

  #watchId: number | null = null
  #permissionStatus: PermissionStatus | null = null
  #watchGeneration = 0
  #watchOptions: LiveLocationWatchOptions = {}

  readonly position = this.#position.asReadonly()
  readonly error = this.#error.asReadonly()
  readonly watching = this.#watching.asReadonly()
  readonly denied = this.#denied.asReadonly()
  readonly unsupported = this.#unsupported.asReadonly()
  readonly lastUpdatedAt = this.#lastUpdatedAt.asReadonly()

  readonly coordinates = computed(() => {
    const position = this.#position()
    if (!position) return null

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy,
      timestamp: position.timestamp
    }
  })

  startWatching(options: LiveLocationWatchOptions = {}): void {
    this.stopWatching()
    const watchGeneration = ++this.#watchGeneration
    this.#watchOptions = options

    if (!navigator.geolocation) {
      this.#unsupported.set(true)
      this.#watching.set(false)
      this.#log('Geolocation is unavailable in this browser')
      return
    }

    const updateAppDevice = options.updateAppDevice ?? true
    this.#unsupported.set(false)
    this.#error.set(null)
    this.#denied.set(false)
    this.#watching.set(true)
    this.#watchPermission(watchGeneration)
    this.#log('Started live location watch')

    this.#watchId = navigator.geolocation.watchPosition(
      (position) => {
        this.#position.set(position)
        this.#lastUpdatedAt.set(new Date(position.timestamp))
        this.#error.set(null)
        this.#denied.set(false)
        this.#deviceService.locationDisabled.set(false)
        this.#deviceService.locationDenied.set(false)

        if (updateAppDevice) {
          this.#appStateService.setDevice(this.#constructDevice(position))
        }
      },
      (error) => {
        this.#error.set(error)
        this.#denied.set(error.code === GEO_PERMISSION_DENIED)
        this.#deviceService.locationDisabled.set(true)
        this.#deviceService.locationDenied.set(error.code === GEO_PERMISSION_DENIED)
        this.#deviceService.browser.set(this.#deviceService.getBrowserInfo().name)
        this.#log('Live location watch failed', {
          code: error.code,
          permissionDenied: error.code === GEO_PERMISSION_DENIED
        })
      },
      {
        enableHighAccuracy: options.enableHighAccuracy ?? true,
        maximumAge: options.maximumAge ?? 0,
        timeout: options.timeout ?? 30000
      }
    )
  }

  stopWatching(): void {
    ++this.#watchGeneration
    if (this.#watchId !== null) {
      navigator.geolocation.clearWatch(this.#watchId)
      this.#watchId = null
      this.#log('Stopped live location watch')
    }
    if (this.#permissionStatus) {
      this.#permissionStatus.onchange = null
      this.#permissionStatus = null
    }
    this.#watching.set(false)
  }

  ngOnDestroy(): void {
    this.stopWatching()
  }

  async #watchPermission(watchGeneration: number): Promise<void> {
    if (!navigator.permissions?.query) {
      return
    }

    try {
      const permissionStatus = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
      if (!this.#watching() || watchGeneration !== this.#watchGeneration) {
        return
      }
      this.#permissionStatus = permissionStatus
      permissionStatus.onchange = () => {
        this.#log('Geolocation permission changed', { state: permissionStatus.state })
        if (permissionStatus.state === 'granted' && this.#watching() && watchGeneration === this.#watchGeneration) {
          this.startWatching(this.#watchOptions)
        }
      }
    } catch {
      this.#log('Unable to observe geolocation permission changes')
    }
  }

  #log(message: string, data?: Record<string, boolean | number | string>): void {
    console.info(`[LiveLocationService] ${message}`, data ?? '')
  }

  #constructDevice(position: GeolocationPosition): Device {
    const deviceType = this.#deviceService.getDeviceType()
    const browserInfo = this.#deviceService.getBrowserInfo()
    const browserIsMobile = deviceType === 'Mobile' || deviceType === 'Tablet'

    return {
      platform: this.#deviceService.getPlatformInfo(),
      deviceType,
      browserName: browserInfo.name,
      userAgent: navigator.userAgent,
      browserIsMobile,
      browserMobileModel: browserIsMobile ? this.#deviceService.getMobileModel() : '',
      geoAccuracy: position.coords.accuracy,
      latitude: position.coords.latitude.toString(),
      longitude: position.coords.longitude.toString()
    }
  }
}
